// src/components/ShopConfig/ShopConfigForm.js
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  CreditCard,
  Store,
  AtSign,
  Key,
  Globe,
  Shield,
  Plus,
  X,
  AlertTriangle,
  Save,
  ChevronDown,
  ChevronUp,
  FileCode,
  FileText,
  Upload,
} from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { getStrategySourceCode } from "../../services/api";
import CounterAutocomplete from "./CounterAutocomplete";

function ShopConfigForm({ initialData, onSubmit, onCancel }) {
  const { actions } = useAppContext();
  const [formData, setFormData] = useState({
    templateName: "",
    shopName: "",
    paymentGateway: "CCAVENUE", // Default gateway (still needed for API)
    easebuzzKey: "",
    easebuzzSalt: "",
    easebuzzUrl: "",
    easebuzzDashboardUrl: "",
    paymentMode: "test",
    ccavenueMerchantId: "",
    ccavenueWorkingKey: "",
    ccavenueAccessCode: "",
    merchantEmail: "",
    isActive: true,
    ccavenueSubAccounts: {
      defaultSubAccountId: "",
      subAccountIds: {},
    },
    omniwareMerchantId: "",
    omniwareApiKey: "",
    omniwareSalt: "",
    strategyInfo: null,
    strategyFile: null,
  });

  // State for enabled payment methods
  const [enabledGateways, setEnabledGateways] = useState({
    ccavenue: false,
    easebuzz: false,
    omniware: false,
  });

  // Additional states for strategy section
  const [showStrategySection, setShowStrategySection] = useState(false);
  const [strategyFileName, setStrategyFileName] = useState("");
  const [existingStrategyCode, setExistingStrategyCode] = useState("");
  const [isLoadingStrategy, setIsLoadingStrategy] = useState(false);

  const [subAccounts, setSubAccounts] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSubAccountsSection, setShowSubAccountsSection] = useState(false);
  const [showCCAvenueSection, setShowCCAvenueSection] = useState(false);
  const [showEasebuzzSection, setShowEasebuzzSection] = useState(false);
  const [showOmniwareSection, setShowOmniwareSection] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);

  // Refs for the select components
  const selectRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize form with data if editing
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);

      // Check which gateways are configured
      const hasCCAvenue = !!(
        initialData.ccavenueMerchantId ||
        initialData.ccavenueWorkingKey ||
        initialData.ccavenueAccessCode
      );

      const hasEasebuzz = !!(
        initialData.easebuzzKey ||
        initialData.easebuzzSalt ||
        initialData.easebuzzUrl
      );

      const hasOmniware = !!(
        initialData.omniwareMerchantId ||
        initialData.omniwareApiKey ||
        initialData.omniwareSalt
      );

      setEnabledGateways({
        ccavenue: hasCCAvenue,
        easebuzz: hasEasebuzz,
        omniware: hasOmniware,
      });

      setShowCCAvenueSection(hasCCAvenue);
      setShowEasebuzzSection(hasEasebuzz);
      setShowOmniwareSection(hasOmniware);

      // Check if there's a strategy file
      if (initialData.strategyInfo) {
        setShowStrategySection(true);

        // Load strategy code if available
        loadStrategyCode(initialData.templateName);
      }

      if (initialData.ccavenueSubAccounts?.subAccountIds) {
        const subAccArr = Object.entries(
          initialData.ccavenueSubAccounts.subAccountIds
        ).map(([storeName, subAccountId]) => ({
          storeName,
          subAccountId,
          isDefault:
            subAccountId ===
            initialData.ccavenueSubAccounts.defaultSubAccountId,
        }));
        setSubAccounts(subAccArr);
        setShowSubAccountsSection(subAccArr.length > 0);
      }
    }
  }, [initialData]);

  // Load strategy code for existing config
  const loadStrategyCode = async (templateName) => {
    if (!templateName) return;

    try {
      setIsLoadingStrategy(true);
      const response = await getStrategySourceCode(templateName);
      if (response && response.sourceCode) {
        setExistingStrategyCode(response.sourceCode);
      }
    } catch (error) {
      console.error("Error loading strategy code:", error);
      // Not setting any error UI here as it's not critical
    } finally {
      setIsLoadingStrategy(false);
    }
  };

  // Add click outside handler for select dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setSelectOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Only accept .cs files
      if (!file.name.endsWith(".cs")) {
        actions.setNotification("Only C# (.cs) files are allowed", "error");
        return;
      }

      setStrategyFileName(file.name);
      setFormData((prev) => ({
        ...prev,
        strategyFile: file,
      }));

      // Clear error if there was one
      if (formErrors.strategyFile) {
        setFormErrors((prev) => ({ ...prev, strategyFile: undefined }));
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const removeSelectedFile = () => {
    setStrategyFileName("");
    setFormData((prev) => ({
      ...prev,
      strategyFile: null,
    }));
    // Reset the file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Close the select dropdown after selection
    if (name === "paymentMode") {
      setSelectOpen(false);
    }

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSwitchChange = (name, checked) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Fixed handlers for section toggles
  const handleCCAvenueToggle = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setShowCCAvenueSection(!showCCAvenueSection);
  };

  const handleEasebuzzToggle = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setShowEasebuzzSection(!showEasebuzzSection);
  };

  const handleOmniwareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowOmniwareSection(!showOmniwareSection);
  };

  const handleSubAccountsToggle = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setShowSubAccountsSection(!showSubAccountsSection);
  };

  const handleStrategyToggle = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    setShowStrategySection(!showStrategySection);
  };

  const handleGatewayToggle = (gateway, enabled) => {
    setEnabledGateways((prev) => ({
      ...prev,
      [gateway]: enabled,
    }));

    // Open/close the corresponding section
    if (gateway === "ccavenue") {
      setShowCCAvenueSection(enabled);
    } else if (gateway === "easebuzz") {
      setShowEasebuzzSection(enabled);
    } else if (gateway === "omniware") {
      setShowOmniwareSection(enabled);
    }

    // Clear errors when disabling a gateway
    if (!enabled) {
      const newErrors = { ...formErrors };

      if (gateway === "ccavenue") {
        delete newErrors.ccavenueMerchantId;
        delete newErrors.ccavenueWorkingKey;
        delete newErrors.ccavenueAccessCode;
        delete newErrors.subAccountDefault;

        for (const key in newErrors) {
          if (key.startsWith("subAccount")) {
            delete newErrors[key];
          }
        }
      } else if (gateway === "easebuzz") {
        delete newErrors.easebuzzKey;
        delete newErrors.easebuzzSalt;
        delete newErrors.easebuzzUrl;
        delete newErrors.paymentMode;
      } else if (gateway === "omniware") {
        delete newErrors.omniwareMerchantId;
        delete newErrors.omniwareApiKey;
        delete newErrors.omniwareSalt;
      }

      setFormErrors(newErrors);
    }
  };

  const addSubAccount = (e) => {
    e.preventDefault();
    setSubAccounts((prev) => [
      ...prev,
      { storeName: "", subAccountId: "", isDefault: prev.length === 0 },
    ]);
    setShowSubAccountsSection(true);
  };

  const removeSubAccount = (index, e) => {
    e.preventDefault();
    setSubAccounts((prev) => {
      const newAccounts = [...prev];
      newAccounts.splice(index, 1);

      // If we removed the default and there are other accounts, make the first one default
      if (prev[index].isDefault && newAccounts.length > 0) {
        newAccounts[0].isDefault = true;
      }

      return newAccounts;
    });
  };

  const handleSubAccountChange = (index, field, value) => {
    setSubAccounts((prev) => {
      const newAccounts = [...prev];
      newAccounts[index] = { ...newAccounts[index], [field]: value };
      return newAccounts;
    });
  };

  const setDefaultSubAccount = (index) => {
    setSubAccounts((prev) =>
      prev.map((acc, idx) => ({
        ...acc,
        isDefault: idx === index,
      }))
    );
  };

  const prepareFormDataForSubmit = () => {
    // Create a copy of form data
    const preparedData = { ...formData };

    // Determine the default gateway
    const enabledCount = [enabledGateways.ccavenue, enabledGateways.easebuzz, enabledGateways.omniware].filter(Boolean).length;
    if (enabledCount === 1) {
      if (enabledGateways.ccavenue) preparedData.paymentGateway = "CCAVENUE";
      else if (enabledGateways.easebuzz) preparedData.paymentGateway = "EASEBUZZ";
      else if (enabledGateways.omniware) preparedData.paymentGateway = "OMNIWARE";
    }
    // If multiple are enabled, keep the selected default in paymentGateway

    // If gateways are disabled, clear their fields
    if (!enabledGateways.ccavenue) {
      preparedData.ccavenueMerchantId = "";
      preparedData.ccavenueWorkingKey = "";
      preparedData.ccavenueAccessCode = "";
      preparedData.ccavenueSubAccounts = {
        defaultSubAccountId: "",
        subAccountIds: {},
      };
    } else {
      // Prepare sub-accounts data if CCAvenue is enabled
      let ccavenueSubAccounts = {
        defaultSubAccountId: "",
        subAccountIds: {},
      };

      if (subAccounts.length > 0) {
        const subAccountsObj = {};
        let defaultId = "";

        for (const acc of subAccounts) {
          subAccountsObj[acc.storeName] = acc.subAccountId;
          if (acc.isDefault) {
            defaultId = acc.subAccountId;
          }
        }

        ccavenueSubAccounts = {
          defaultSubAccountId: defaultId,
          subAccountIds: subAccountsObj,
        };
      }

      preparedData.ccavenueSubAccounts = ccavenueSubAccounts;
    }

    if (!enabledGateways.easebuzz) {
      preparedData.easebuzzKey = "";
      preparedData.easebuzzSalt = "";
      preparedData.easebuzzUrl = "";
      preparedData.easebuzzDashboardUrl = "";
    }

    // Reset paymentMode only when no gateways are enabled
    if (!enabledGateways.easebuzz && !enabledGateways.omniware && !enabledGateways.ccavenue) {
      preparedData.paymentMode = "test";
    }

    if (!enabledGateways.omniware) {
      preparedData.omniwareMerchantId = "";
      preparedData.omniwareApiKey = "";
      preparedData.omniwareSalt = "";
    }

    return preparedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorEl = document.querySelector(".error-message");
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSubmitting(true);

    try {
      const dataToSubmit = prepareFormDataForSubmit();
      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation for all payment gateways
    if (!formData.templateName.trim())
      errors.templateName = "Template name is required";
    if (!formData.shopName.trim()) errors.shopName = "Shop name is required";
    if (!formData.merchantEmail.trim())
      errors.merchantEmail = "Merchant email is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.merchantEmail && !emailRegex.test(formData.merchantEmail)) {
      errors.merchantEmail = "Please enter a valid email address";
    }

    // At least one gateway must be enabled
    if (!enabledGateways.ccavenue && !enabledGateways.easebuzz && !enabledGateways.omniware) {
      errors.paymentGateway = "At least one payment gateway must be enabled";
    }

    // Validate enabled gateways
    if (enabledGateways.ccavenue) {
      if (!formData.ccavenueMerchantId.trim())
        errors.ccavenueMerchantId = "Merchant ID is required";
      if (!formData.ccavenueWorkingKey.trim())
        errors.ccavenueWorkingKey = "Working key is required";
      if (!formData.ccavenueAccessCode.trim())
        errors.ccavenueAccessCode = "Access code is required";

      // Validate sub-accounts if they exist
      if (subAccounts.length > 0) {
        let hasDefault = false;

        for (let i = 0; i < subAccounts.length; i++) {
          if (!subAccounts[i].storeName.trim()) {
            errors[`subAccount${i}Store`] = "Store name is required";
          }
          if (!subAccounts[i].subAccountId.trim()) {
            errors[`subAccount${i}Id`] = "Sub-account ID is required";
          }
          if (subAccounts[i].isDefault) {
            hasDefault = true;
          }
        }

        if (subAccounts.length > 0 && !hasDefault) {
          errors.subAccountDefault = "Please select a default sub-account";
        }
      }
    }

    if (enabledGateways.easebuzz) {
      if (!formData.easebuzzKey.trim())
        errors.easebuzzKey = "Easebuzz key is required";
      if (!formData.easebuzzSalt.trim())
        errors.easebuzzSalt = "Easebuzz salt is required";
      if (!formData.easebuzzUrl.trim())
        errors.easebuzzUrl = "Easebuzz URL is required";
      if (!formData.paymentMode)
        errors.paymentMode = "Payment mode is required";
    }

    // Payment mode required when any gateway is enabled
    if ((enabledGateways.omniware || enabledGateways.ccavenue) && !enabledGateways.easebuzz) {
      if (!formData.paymentMode)
        errors.paymentMode = "Payment mode is required";
    }

    if (enabledGateways.omniware) {
      if (!formData.omniwareMerchantId.trim())
        errors.omniwareMerchantId = "Merchant ID is required";
      if (!formData.omniwareApiKey.trim())
        errors.omniwareApiKey = "API Key is required";
      if (!formData.omniwareSalt.trim())
        errors.omniwareSalt = "Salt is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to render form error message
  const ErrorMessage = ({ message }) => {
    if (!message) return null;

    return (
      <p className="mt-1 text-sm text-red-600 error-message flex items-center">
        <AlertTriangle className="h-3 w-3 mr-1" />
        {message}
      </p>
    );
  };

  // Custom Select implementation
  const CustomSelect = ({ value, onChange, options }) => {
    return (
      <div className="relative" ref={selectRef}>
        <div
          className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSelectOpen(!selectOpen);
          }}
        >
          <span>
            {options.find((opt) => opt.value === value)?.label ||
              "Select an option"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>

        {selectOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
            {options.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(option.value);
                  setSelectOpen(false);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="templateName" className="flex items-center">
                    Template Name <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <CounterAutocomplete
                    value={formData.templateName}
                    onChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        templateName: value?.activationKey,
                      }));
                      // Clear error when field is changed
                      if (formErrors.templateName) {
                        setFormErrors((prev) => ({
                          ...prev,
                          templateName: undefined,
                        }));
                      }
                    }}
                    onSelectCounter={(counter) => {
                      // Auto-fill other fields based on counter data
                      setFormData((prev) => ({
                        ...prev,
                        templateName: counter.activationKey,
                        shopName: counter.shopName || prev.shopName,
                        // You can populate other fields from counter data if needed
                      }));

                      // Clear errors for updated fields
                      setFormErrors((prev) => ({
                        ...prev,
                        templateName: undefined,
                        shopName: undefined,
                      }));
                    }}
                  />
                  <ErrorMessage message={formErrors.templateName} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shopName" className="flex items-center">
                    Shop Name <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Store className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      id="shopName"
                      name="shopName"
                      value={formData.shopName}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="e.g., Coffee Haven"
                    />
                  </div>
                  <ErrorMessage message={formErrors.shopName} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="merchantEmail" className="flex items-center">
                    Merchant Email <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <AtSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      id="merchantEmail"
                      name="merchantEmail"
                      type="email"
                      value={formData.merchantEmail}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder="merchant@example.com"
                    />
                  </div>
                  <ErrorMessage message={formErrors.merchantEmail} />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center">
                    Default Payment Gateway{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>

                  {/* Fixed RadioGroup implementation */}
                  <div className="flex flex-col space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentGateway"
                        value="CCAVENUE"
                        checked={formData.paymentGateway === "CCAVENUE"}
                        onChange={() =>
                          handleSelectChange("paymentGateway", "CCAVENUE")
                        }
                        disabled={!enabledGateways.ccavenue}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span
                        className={`${
                          !enabledGateways.ccavenue
                            ? "text-gray-400"
                            : "text-gray-700"
                        }`}
                      >
                        CCAvenue
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentGateway"
                        value="EASEBUZZ"
                        checked={formData.paymentGateway === "EASEBUZZ"}
                        onChange={() =>
                          handleSelectChange("paymentGateway", "EASEBUZZ")
                        }
                        disabled={!enabledGateways.easebuzz}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span
                        className={`${
                          !enabledGateways.easebuzz
                            ? "text-gray-400"
                            : "text-gray-700"
                        }`}
                      >
                        Easebuzz
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentGateway"
                        value="OMNIWARE"
                        checked={formData.paymentGateway === "OMNIWARE"}
                        onChange={() =>
                          handleSelectChange("paymentGateway", "OMNIWARE")
                        }
                        disabled={!enabledGateways.omniware}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span
                        className={`${
                          !enabledGateways.omniware
                            ? "text-gray-400"
                            : "text-gray-700"
                        }`}
                      >
                        Omniware
                      </span>
                    </label>
                  </div>

                  <ErrorMessage message={formErrors.paymentGateway} />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="isActive" className="flex items-center">
                      Active Status
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="isActive"
                        checked={formData.isActive}
                        onCheckedChange={(checked) =>
                          handleSwitchChange("isActive", checked)
                        }
                      />
                      <span className="text-sm text-gray-500">
                        {formData.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    When inactive, this configuration will not be used for
                    payments.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Gateways Section */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Payment Gateways
              </h3>

              <div className="mb-6 flex flex-col space-y-4">
                <Label className="text-md font-medium">
                  Enable Payment Gateways{" "}
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Select the payment gateways you want to configure for this
                  shop. At least one gateway must be enabled.
                </p>
                <ErrorMessage message={formErrors.paymentGateway} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  {/* CCAvenue Card */}
                  <Card
                    className={`${
                      enabledGateways.ccavenue
                        ? "border-blue-500 ring-1 ring-blue-500"
                        : "border-gray-200"
                    } transition-all`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <CreditCard className="h-5 w-5 text-gray-600" />
                          <h4 className="font-medium">CCAvenue</h4>
                        </div>
                        <div className="flex items-center">
                          <Switch
                            id="enable-ccavenue"
                            checked={enabledGateways.ccavenue}
                            onCheckedChange={(checked) =>
                              handleGatewayToggle("ccavenue", checked)
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Configure CCAvenue for secure credit card, debit card,
                        and net banking transactions.
                      </p>

                      {/* Fixed button with explicit type and handler */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCCAvenueToggle}
                        className="mt-3 text-xs w-full"
                        disabled={!enabledGateways.ccavenue}
                      >
                        {showCCAvenueSection ? "Hide Details" : "Configure"}
                        {showCCAvenueSection ? (
                          <ChevronUp className="h-3 w-3 ml-1" />
                        ) : (
                          <ChevronDown className="h-3 w-3 ml-1" />
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Easebuzz Card */}
                  <Card
                    className={`${
                      enabledGateways.easebuzz
                        ? "border-blue-500 ring-1 ring-blue-500"
                        : "border-gray-200"
                    } transition-all`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <CreditCard className="h-5 w-5 text-gray-600" />
                          <h4 className="font-medium">Easebuzz</h4>
                        </div>
                        <div className="flex items-center">
                          <Switch
                            id="enable-easebuzz"
                            checked={enabledGateways.easebuzz}
                            onCheckedChange={(checked) =>
                              handleGatewayToggle("easebuzz", checked)
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Configure Easebuzz for UPI, wallet, and other payment
                        options.
                      </p>

                      {/* Fixed button with explicit type and handler */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleEasebuzzToggle}
                        className="mt-3 text-xs w-full"
                        disabled={!enabledGateways.easebuzz}
                      >
                        {showEasebuzzSection ? "Hide Details" : "Configure"}
                        {showEasebuzzSection ? (
                          <ChevronUp className="h-3 w-3 ml-1" />
                        ) : (
                          <ChevronDown className="h-3 w-3 ml-1" />
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Omniware Card */}
                  <Card
                    className={`${
                      enabledGateways.omniware
                        ? "border-blue-500 ring-1 ring-blue-500"
                        : "border-gray-200"
                    } transition-all`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <CreditCard className="h-5 w-5 text-gray-600" />
                          <h4 className="font-medium">Omniware</h4>
                        </div>
                        <div className="flex items-center">
                          <Switch
                            id="enable-omniware"
                            checked={enabledGateways.omniware}
                            onCheckedChange={(checked) =>
                              handleGatewayToggle("omniware", checked)
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Configure Omniware for UPI, cards, net banking, and
                        wallet payments.
                      </p>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleOmniwareToggle}
                        className="mt-3 text-xs w-full"
                        disabled={!enabledGateways.omniware}
                      >
                        {showOmniwareSection ? "Hide Details" : "Configure"}
                        {showOmniwareSection ? (
                          <ChevronUp className="h-3 w-3 ml-1" />
                        ) : (
                          <ChevronDown className="h-3 w-3 ml-1" />
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* CCAvenue Settings */}
              {enabledGateways.ccavenue && showCCAvenueSection && (
                <div className="mt-6 border p-4 rounded-md">
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    CCAvenue Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="ccavenueMerchantId"
                        className="flex items-center"
                      >
                        Merchant ID <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Key className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="ccavenueMerchantId"
                          name="ccavenueMerchantId"
                          value={formData.ccavenueMerchantId}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.ccavenueMerchantId} />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="ccavenueAccessCode"
                        className="flex items-center"
                      >
                        Access Code <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Shield className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="ccavenueAccessCode"
                          name="ccavenueAccessCode"
                          value={formData.ccavenueAccessCode}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.ccavenueAccessCode} />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label
                        htmlFor="ccavenueWorkingKey"
                        className="flex items-center"
                      >
                        Working Key <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Key className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="ccavenueWorkingKey"
                          name="ccavenueWorkingKey"
                          type="password"
                          value={formData.ccavenueWorkingKey}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.ccavenueWorkingKey} />
                    </div>

                    {/* CCAvenue Sub-Accounts Section */}
                    <div className="md:col-span-2 pt-2">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-medium text-gray-900">
                          Sub-Accounts
                        </h4>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSubAccountsToggle}
                          className="flex items-center"
                        >
                          {showSubAccountsSection ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              Hide
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              {subAccounts.length > 0
                                ? `Show (${subAccounts.length})`
                                : "Add"}
                            </>
                          )}
                        </Button>
                      </div>

                      {showSubAccountsSection && (
                        <div className="space-y-4">
                          {subAccounts.map((subAccount, index) => (
                            <div
                              key={index}
                              className="p-4 border border-gray-200 rounded-md bg-gray-50"
                            >
                              <div className="flex justify-between items-center mb-2">
                                <h5 className="text-sm font-medium">
                                  Sub-Account #{index + 1}
                                </h5>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => removeSubAccount(index, e)}
                                  className="h-8 w-8 p-0 text-red-500"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`storeName${index}`}>
                                    Store Name
                                  </Label>
                                  <Input
                                    id={`storeName${index}`}
                                    value={subAccount.storeName}
                                    onChange={(e) =>
                                      handleSubAccountChange(
                                        index,
                                        "storeName",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Store name"
                                  />
                                  <ErrorMessage
                                    message={
                                      formErrors[`subAccount${index}Store`]
                                    }
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`subAccountId${index}`}>
                                    Sub-Account ID
                                  </Label>
                                  <Input
                                    id={`subAccountId${index}`}
                                    value={subAccount.subAccountId}
                                    onChange={(e) =>
                                      handleSubAccountChange(
                                        index,
                                        "subAccountId",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Sub-account ID"
                                  />
                                  <ErrorMessage
                                    message={formErrors[`subAccount${index}Id`]}
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="radio"
                                      id={`defaultSubAccount${index}`}
                                      name="defaultSubAccount"
                                      checked={subAccount.isDefault}
                                      onChange={() =>
                                        setDefaultSubAccount(index)
                                      }
                                      className="h-4 w-4 text-blue-600"
                                    />
                                    <Label
                                      htmlFor={`defaultSubAccount${index}`}
                                      className="text-sm"
                                    >
                                      Set as default sub-account
                                    </Label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          <ErrorMessage
                            message={formErrors.subAccountDefault}
                          />

                          <Button
                            type="button"
                            variant="outline"
                            onClick={addSubAccount}
                            className="w-full mt-2 flex items-center justify-center"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Sub-Account
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Easebuzz Settings */}
              {enabledGateways.easebuzz && showEasebuzzSection && (
                <div className="mt-6 border p-4 rounded-md">
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    Easebuzz Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="easebuzzKey"
                        className="flex items-center"
                      >
                        Easebuzz Key{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Key className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="easebuzzKey"
                          name="easebuzzKey"
                          value={formData.easebuzzKey}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.easebuzzKey} />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="easebuzzSalt"
                        className="flex items-center"
                      >
                        Easebuzz Salt{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Shield className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="easebuzzSalt"
                          name="easebuzzSalt"
                          type="password"
                          value={formData.easebuzzSalt}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.easebuzzSalt} />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="easebuzzUrl"
                        className="flex items-center"
                      >
                        Easebuzz URL{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Globe className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="easebuzzUrl"
                          name="easebuzzUrl"
                          value={formData.easebuzzUrl}
                          onChange={handleInputChange}
                          className="pl-10"
                          placeholder="https://pay.easebuzz.in/"
                        />
                      </div>
                      <ErrorMessage message={formErrors.easebuzzUrl} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="easebuzzDashboardUrl">
                        Dashboard URL
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Globe className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="easebuzzDashboardUrl"
                          name="easebuzzDashboardUrl"
                          value={formData.easebuzzDashboardUrl}
                          onChange={handleInputChange}
                          className="pl-10"
                          placeholder="https://dashboard.easebuzz.in/"
                        />
                      </div>
                    </div>

                  </div>
                </div>
              )}

              {/* Payment Mode - shared across all gateways */}
              {(enabledGateways.easebuzz || enabledGateways.omniware || enabledGateways.ccavenue) && (
                <div className="mt-6 border p-4 rounded-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="paymentMode"
                        className="flex items-center"
                      >
                        Payment Mode{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>

                      {/* Custom dropdown replacement for Select component */}
                      <CustomSelect
                        value={formData.paymentMode}
                        onChange={(value) =>
                          handleSelectChange("paymentMode", value)
                        }
                        options={[
                          { value: "test", label: "Test" },
                          { value: "production", label: "Production" },
                        ]}
                      />

                      <ErrorMessage message={formErrors.paymentMode} />
                    </div>
                  </div>
                </div>
              )}

              {/* Omniware Settings */}
              {enabledGateways.omniware && showOmniwareSection && (
                <div className="mt-6 border p-4 rounded-md">
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    Omniware Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="omniwareMerchantId"
                        className="flex items-center"
                      >
                        Merchant ID{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Key className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="omniwareMerchantId"
                          name="omniwareMerchantId"
                          value={formData.omniwareMerchantId}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.omniwareMerchantId} />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="omniwareApiKey"
                        className="flex items-center"
                      >
                        API Key{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Key className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="omniwareApiKey"
                          name="omniwareApiKey"
                          type="password"
                          value={formData.omniwareApiKey}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.omniwareApiKey} />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label
                        htmlFor="omniwareSalt"
                        className="flex items-center"
                      >
                        Salt{" "}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Shield className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="omniwareSalt"
                          name="omniwareSalt"
                          type="password"
                          value={formData.omniwareSalt}
                          onChange={handleInputChange}
                          className="pl-10"
                        />
                      </div>
                      <ErrorMessage message={formErrors.omniwareSalt} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Strategy File Upload Section */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Processing Strategy
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleStrategyToggle}
                  className="flex items-center"
                >
                  {showStrategySection ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Hide
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      {formData.strategyInfo ? "Edit" : "Add"}
                    </>
                  )}
                </Button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Upload a custom C# strategy file to define how invoice data is
                extracted. This strategy will be used when processing invoices
                for this shop.
              </p>

              {showStrategySection && (
                <div className="space-y-4 border p-4 rounded-md">
                  {/* Hidden file input - actual input used for file selection */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".cs"
                    className="hidden"
                  />

                  {/* Current Strategy Display */}
                  {formData.strategyInfo && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h4 className="text-md font-medium text-blue-700 flex items-center">
                        <FileCode className="h-4 w-4 mr-2" />
                        Current Strategy
                      </h4>
                      <div className="mt-2">
                        <p className="text-sm text-blue-600">
                          <span className="font-medium">Class:</span>{" "}
                          {formData.strategyInfo.className}
                        </p>
                        <p className="text-sm text-blue-600">
                          <span className="font-medium">Last Updated:</span>{" "}
                          {new Date(
                            formData.strategyInfo.uploadDate
                          ).toLocaleString()}
                        </p>
                      </div>

                      {/* Preview code button */}
                      {existingStrategyCode && (
                        <div className="mt-4 border border-blue-200 rounded bg-white p-3">
                          <h5 className="text-sm font-medium text-blue-700 mb-2">
                            Code Preview:
                          </h5>
                          <div className="bg-gray-800 text-gray-100 p-3 rounded max-h-40 overflow-y-auto">
                            <pre className="text-xs">
                              {existingStrategyCode.slice(0, 500)}
                              {existingStrategyCode.length > 500 ? "..." : ""}
                            </pre>
                          </div>
                        </div>
                      )}

                      {isLoadingStrategy && (
                        <div className="mt-4 flex items-center text-blue-600">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                          Loading strategy code...
                        </div>
                      )}
                    </div>
                  )}

                  {/* File Upload Area */}
                  <div className="mt-4">
                    <Label htmlFor="strategyFile">
                      Upload Strategy File (.cs)
                    </Label>

                    {!strategyFileName ? (
                      <div
                        className="mt-2 border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                        onClick={triggerFileInput}
                      >
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">
                          Click to upload a C# strategy file
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Only .cs files are supported
                        </p>
                      </div>
                    ) : (
                      <div className="mt-2 border border-gray-200 rounded-md p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FileCode className="h-5 w-5 text-blue-500 mr-2" />
                            <span className="text-sm font-medium">
                              {strategyFileName}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeSelectedFile}
                            className="h-8 w-8 p-0 text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                    <h5 className="text-sm font-medium text-blue-700 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Requirements
                    </h5>
                    <ul className="mt-2 text-xs text-blue-600 space-y-1 list-disc pl-5">
                      <li>
                        File must contain a class that implements
                        IInvoiceParsingStrategy
                      </li>
                      <li>
                        Class must have a method called ExtractInvoiceDetails
                      </li>
                      <li>Make sure the namespace is correctly defined</li>
                      <li>File size should be less than 500KB</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6 border-t border-gray-200 flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={submitting}
                className="flex items-center"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
              >
                {submitting ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {initialData ? "Update" : "Save"} Configuration
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

export default ShopConfigForm;
